// content messages. Keys are prefixed `content.`; add every English string to contentEn and its Indonesian translation to contentId.
export const contentEn = {
  // Content types (singular / plural), one pair per ContentResourceKey.
  'content.res.pages.singular': 'Page',
  'content.res.pages.plural': 'Pages',
  'content.res.articles.singular': 'Article',
  'content.res.articles.plural': 'Articles',
  'content.res.products.singular': 'Product',
  'content.res.products.plural': 'Products',
  'content.res.product-categories.singular': 'Category',
  'content.res.product-categories.plural': 'Categories',
  'content.res.industries.singular': 'Industry',
  'content.res.industries.plural': 'Industries',
  'content.res.swatches.singular': 'Item',
  'content.res.swatches.plural': 'Materials & Finishes',

  // Shared field labels
  'content.field.name': 'Name',
  'content.field.title': 'Title',
  'content.field.summary': 'Summary',
  'content.field.description': 'Description',
  'content.field.heroImage': 'Hero image',
  'content.field.gallery': 'Gallery',
  'content.field.sortOrder': 'Sort order',
  'content.field.additionalContent': 'Additional content',
  'content.field.sectionHeading': 'Section heading',
  'content.field.status': 'Status',

  // List screen
  'content.list.total': '{count} total',
  'content.list.searchPlaceholder': 'Search by title or slug',
  'content.list.section': 'Section',
  'content.list.allSections': 'All sections',
  'content.list.journal': 'Journal',
  'content.list.news': 'News',
  'content.list.kind': 'Kind',
  'content.list.kindBoth': 'Both',
  'content.list.kindMaterials': 'Materials',
  'content.list.kindFinishes': 'Finishes',
  'content.list.allStatuses': 'All statuses',
  'content.list.emptyFiltered': 'Nothing matches these filters',
  'content.list.emptyFilteredHint': 'Try a different search or status.',
  'content.list.emptyTitle': 'No {items} yet',
  'content.list.emptyHint': 'Create the first {item} to get started.',
  'content.list.colTitle': 'Title',
  'content.list.colStatus': 'Status',
  'content.list.colDetails': 'Details',
  'content.list.colUpdated': 'Updated',
  'content.list.colPublished': 'Published on',
  'content.list.colCreated': 'Created on',
  'content.list.scheduled': 'Scheduled',
  'content.list.sortBy': 'Sort by {column}',
  'content.list.colActions': 'Actions',
  'content.list.view': 'View',
  'content.list.deleteTitle': 'Delete “{name}”?',
  'content.list.deleteBody': 'This cannot be undone. Consider archiving instead.',
  'content.list.pagination': 'Pagination',
  'content.list.previous': '← Previous',
  'content.list.next': 'Next →',
  'content.list.pageOf': 'Page {page} of {pages} · {total} total',

  // Editor screen
  'content.editor.new': 'New {item}',
  'content.editor.crumbEdit': 'Edit',
  'content.editor.crumbNew': 'New',
  'content.editor.lastSaved': 'Last saved {date}',
  'content.editor.viewLive': 'View live',
  'content.editor.update': 'Update',
  'content.editor.saveDraft': 'Save draft',
  'content.editor.publish': 'Publish',
  'content.editor.requiredPrimary': 'Required in the primary language',
  'content.editor.needsAttention': 'Some fields need attention.',
  'content.editor.itemPublished': '{item} published',
  'content.editor.changesSaved': 'Changes saved',
  'content.editor.itemDeleted': '{item} deleted',
  'content.editor.deleteTitle': 'Delete this {item}?',
  'content.editor.deleteBody': 'This cannot be undone.',
  'content.editor.deleteItem': 'Delete {item}',
  'content.editor.publishing': 'Publishing',
  'content.editor.statusDraft': 'Draft — hidden from the site',
  'content.editor.statusPublished': 'Published — live',
  'content.editor.statusArchived': 'Archived — hidden, kept for reference',
  'content.editor.slug': 'URL slug',
  'content.editor.slugHint': 'Unique within {material} or within {finish}.',
  'content.editor.homeSlug': 'Slug “home” renders at the site root.',
  'content.editor.firstPublished': 'First published {date}',
  'content.editor.back': 'Back',

  // SEO panel
  'content.seo.title': 'Search & Social',
  'content.seo.description': 'How this appears on Google and when shared.',
  'content.seo.untitled': 'Untitled',
  'content.seo.addDescription': 'Add a description so search results show a summary.',
  'content.seo.metaTitle': 'Meta title',
  'content.seo.metaTitleHint': 'Leave empty to use the page title.',
  'content.seo.metaDescription': 'Meta description',
  'content.seo.shareImage': 'Social share image',
  'content.seo.shareImageHint': '1200 × 630 recommended. Falls back to the main image.',
  'content.seo.canonicalPath': 'Canonical path',
  'content.seo.canonicalHint': 'Only set when this content lives primarily at another URL.',
  'content.seo.noindex': 'Hide from search engines (noindex)',

  // FAQ editor
  'content.faq.title': 'FAQs',
  'content.faq.description': 'Shown as an accordion and added to Google’s FAQ rich results.',
  'content.faq.add': '+ Add FAQ',
  'content.faq.empty': 'No FAQs yet.',
  'content.faq.questionN': 'Question {n}',
  'content.faq.remove': 'Remove',
  'content.faq.question': 'Question',
  'content.faq.answer': 'Answer',

  // Localized fields
  'content.localized.fallsBackTo': 'Falls back to {language}',
  'content.localized.richPlaceholder': 'Empty — the {language} version is shown on the site',
  'content.localized.startFrom': 'Start from the {language} text',

  // Details editor: packaging sections
  'content.section.capabilityStrip': 'Capability badges',
  'content.section.overview': 'Overview',
  'content.section.children': 'Products in this category',
  'content.section.structures': 'Structures',
  'content.section.gallery': 'Gallery',
  'content.section.swatches': 'Material & finish swatches',
  'content.section.materialsFinishes': 'Materials & finishes',
  'content.section.specs': 'Specifications',
  'content.section.cost': 'Cost note',
  'content.section.design': 'Customisation',
  'content.section.applications': 'Applications',
  'content.section.process': 'Process',
  'content.section.why': 'Why us (trust points)',
  'content.section.faq': 'FAQs',
  'content.section.related': 'Related articles',
  'content.section.cta': 'Call to action',

  // Packaging groups (swatch applicability)
  'content.specGroup.rigid': 'Rigid boxes',
  'content.specGroup.label': 'Labels',
  'content.specGroup.carton': 'Folding cartons',
  'content.specGroup.corrugated': 'Corrugated',
  'content.specGroup.bag': 'Paper bags',
  'content.specGroup.nonwoven': 'Non-woven bags',
  'content.specGroup.tube': 'Paper tubes',
  'content.specGroup.component': 'Printed components',

  // Details editor
  'content.details.specsEmpty': 'Add rows like Board / 1200gsm greyboard.',
  'content.details.label': 'Label',
  'content.details.value': 'Value',
  'content.details.addSpecRow': '+ Spec row',
  'content.details.headerPanel': 'Page Header',
  'content.details.headerPanelHint': 'H1 and subtitle at the top of the page, plus the capability strip.',
  'content.details.heading': 'Heading (H1)',
  'content.details.headingHint': 'Leave empty to use the name.',
  'content.details.subtitle': 'Subtitle',
  'content.details.retailPanel': 'Product details',
  'content.details.codeLine': 'Code line',
  'content.details.codeLineHint': 'Small grey line under the name, e.g. "AUR-R101 · 18K white gold".',
  'content.details.includes': 'Included with every purchase',
  'content.details.includesHint': 'Short points shown next to the specifications, e.g. "Insured shipping".',
  'content.details.badges': 'Capability badges',
  'content.details.badge': 'Badge',
  'content.details.addBadge': '+ Badge',
  'content.details.overviewPanel': 'Overview',
  'content.details.paragraph': 'Paragraph',
  'content.details.addParagraph': '+ Paragraph',
  'content.details.structuresPanel': 'Structures',
  'content.details.structure': 'Structure',
  'content.details.addStructure': '+ Structure',
  'content.details.materialsFinishesPanel': 'Materials & Finishes',
  'content.details.materials': 'Materials',
  'content.details.material': 'Material',
  'content.details.addMaterial': '+ Material',
  'content.details.finishes': 'Finishes',
  'content.details.finish': 'Finish',
  'content.details.addFinish': '+ Finish',
  'content.details.swatchGroups': 'Swatch groups',
  'content.details.specsPanel': 'Specifications & Customisation',
  'content.details.specs': 'Specifications',
  'content.details.customisation': 'Customisation options',
  'content.details.option': 'Option',
  'content.details.addOption': '+ Option',
  'content.details.costNote': 'Cost note',
  'content.details.applicationsPanel': 'Applications, Process & Trust',
  'content.details.applications': 'Applications',
  'content.details.application': 'Application',
  'content.details.addApplication': '+ Application',
  'content.details.processSteps': 'Process steps',
  'content.details.step': 'Step',
  'content.details.addStep': '+ Step',
  'content.details.trust': 'Why us (trust points)',
  'content.details.point': 'Point',
  'content.details.addPoint': '+ Point',
  'content.details.layoutPanel': 'Page Layout',
  'content.details.layoutHint': 'Sections render in this order. Empty sections are skipped.',
  'content.details.relatedProducts': 'Related products',
  'content.details.relatedProductsHint': 'Catalogue paths such as /products/labels. Titles follow the linked page.',
  'content.details.pageN': 'Page {n}',
  'content.details.addRelatedPage': '+ Related page',
  'content.details.relatedArticles': 'Related articles',

  // Forms: page
  'content.form.basics': 'Basics',
  'content.form.pageTitle': 'Page title',
  'content.form.pageTitleHint': 'Used in the browser tab and as the default SEO title.',

  // Forms: article
  'content.form.article': 'Article',
  'content.form.excerpt': 'Excerpt',
  'content.form.excerptHint': 'Shown on listing cards. Generated from the body when empty.',
  'content.form.body': 'Body',
  'content.form.details': 'Details',
  'content.form.section': 'Section',
  'content.form.sectionJournal': 'Journal (guides & insights)',
  'content.form.sectionNews': 'News (company updates)',
  'content.form.categoryLabel': 'Category label',
  'content.form.categoryLabelPlaceholder': 'Guide',
  'content.form.author': 'Author',
  'content.form.publishDate': 'Publish date',
  'content.form.publishDateHint': 'Future dates schedule the article. Empty = when first published.',
  'content.form.featured': 'Featured',

  // Forms: pickers
  'content.form.category': 'Category',
  'content.form.chooseCategory': 'Choose a category',
  'content.form.nothingToChoose': 'Nothing to choose yet.',

  // Forms: product
  'content.form.product': 'Product',
  'content.form.productSummaryHint': 'Card blurb on the category page and default meta description.',
  'content.form.organisation': 'Organisation',
  'content.form.sortOrderHint': 'Lower numbers show first.',
  'content.form.industries': 'Industries',

  // Forms: category
  'content.form.categoryPanel': 'Category',
  'content.form.tagline': 'Tagline',
  'content.form.taglineHint': 'Short line under the name on the products grid.',
  'content.form.ordering': 'Ordering',
  'content.form.categoryOrderHint': 'Categories are listed 01, 02, … in this order.',

  // Forms: industry
  'content.form.industry': 'Industry',
  'content.form.risk': 'Packaging risk',
  'content.form.riskHint': 'One line shown on the industries grid.',
  'content.form.cardLine': 'Card line',
  'content.form.cardLineHint': 'One short line under the name on the overview page.',
  'content.form.noteRetail': 'Note',
  'content.form.noteRetailHint': 'Anything worth knowing, e.g. care or how it wears.',
  'content.form.recommendedCategories': 'Recommended categories',
  'content.form.recommendedProducts': 'Recommended products',

  // Forms: swatch
  'content.form.swatch': 'Swatch',
  'content.form.swatchDescriptionHint': 'One line under the name.',
  'content.form.note': 'Buyer’s note',
  'content.form.noteHint': 'When to choose it, and what to watch for.',
  'content.form.photo': 'Photo',
  'content.form.guideLink': 'Guide link (optional)',
  'content.form.guideLinkText': 'Guide link text',
  'content.form.guideLinkTextPlaceholder': 'Read the foil stamping guide',
  'content.form.whereItAppears': 'Where it appears',
  'content.form.whereItAppearsHint': 'Product, category and industry pages list the swatches for their own packaging groups.',
  'content.form.recommendedRank': 'Recommended rank',
  'content.form.recommendedRankHint': '0 lists it first; empty = not a featured pick',
  'content.form.library': 'Library',
  'content.form.kind': 'Kind',
  'content.form.kindMaterial': 'Material (board, paper, film…)',
  'content.form.kindFinish': 'Finish (foil, emboss, coating…)',
  'content.form.groupKey': 'Group key',
  'content.form.groupKeyHint': 'Items with the same key are shown together under one heading on the website.',
  'content.form.swatchSortHint': 'Lower numbers first.',
} as const

export const contentId: Record<keyof typeof contentEn, string> = {
  'content.res.pages.singular': 'Halaman',
  'content.res.pages.plural': 'Halaman',
  'content.res.articles.singular': 'Artikel',
  'content.res.articles.plural': 'Artikel',
  'content.res.products.singular': 'Produk',
  'content.res.products.plural': 'Produk',
  'content.res.product-categories.singular': 'Kategori',
  'content.res.product-categories.plural': 'Kategori',
  'content.res.industries.singular': 'Industri',
  'content.res.industries.plural': 'Industri',
  'content.res.swatches.singular': 'Item',
  'content.res.swatches.plural': 'Bahan & Finishing',

  'content.field.name': 'Nama',
  'content.field.title': 'Judul',
  'content.field.summary': 'Ringkasan',
  'content.field.description': 'Deskripsi',
  'content.field.heroImage': 'Gambar utama',
  'content.field.gallery': 'Galeri',
  'content.field.sortOrder': 'Urutan',
  'content.field.additionalContent': 'Konten tambahan',
  'content.field.sectionHeading': 'Judul bagian',
  'content.field.status': 'Status',

  'content.list.total': '{count} total',
  'content.list.searchPlaceholder': 'Cari berdasarkan judul atau slug',
  'content.list.section': 'Bagian',
  'content.list.allSections': 'Semua bagian',
  'content.list.journal': 'Jurnal',
  'content.list.news': 'Berita',
  'content.list.kind': 'Jenis',
  'content.list.kindBoth': 'Keduanya',
  'content.list.kindMaterials': 'Bahan',
  'content.list.kindFinishes': 'Finishing',
  'content.list.allStatuses': 'Semua status',
  'content.list.emptyFiltered': 'Tidak ada yang cocok dengan filter ini',
  'content.list.emptyFilteredHint': 'Coba kata kunci atau status lain.',
  'content.list.emptyTitle': 'Belum ada {items}',
  'content.list.emptyHint': 'Buat {item} pertama untuk memulai.',
  'content.list.colTitle': 'Judul',
  'content.list.colStatus': 'Status',
  'content.list.colDetails': 'Detail',
  'content.list.colUpdated': 'Diperbarui',
  'content.list.colPublished': 'Terbit pada',
  'content.list.colCreated': 'Dibuat pada',
  'content.list.scheduled': 'Terjadwal',
  'content.list.sortBy': 'Urutkan menurut {column}',
  'content.list.colActions': 'Aksi',
  'content.list.view': 'Lihat',
  'content.list.deleteTitle': 'Hapus “{name}”?',
  'content.list.deleteBody': 'Tindakan ini tidak dapat dibatalkan. Pertimbangkan untuk mengarsipkannya saja.',
  'content.list.pagination': 'Navigasi halaman',
  'content.list.previous': '← Sebelumnya',
  'content.list.next': 'Berikutnya →',
  'content.list.pageOf': 'Halaman {page} dari {pages} · {total} total',

  'content.editor.new': '{item} baru',
  'content.editor.crumbEdit': 'Ubah',
  'content.editor.crumbNew': 'Baru',
  'content.editor.lastSaved': 'Terakhir disimpan {date}',
  'content.editor.viewLive': 'Lihat di situs',
  'content.editor.update': 'Perbarui',
  'content.editor.saveDraft': 'Simpan draf',
  'content.editor.publish': 'Terbitkan',
  'content.editor.requiredPrimary': 'Wajib diisi dalam bahasa utama',
  'content.editor.needsAttention': 'Beberapa kolom perlu diperhatikan.',
  'content.editor.itemPublished': '{item} diterbitkan',
  'content.editor.changesSaved': 'Perubahan disimpan',
  'content.editor.itemDeleted': '{item} dihapus',
  'content.editor.deleteTitle': 'Hapus {item} ini?',
  'content.editor.deleteBody': 'Tindakan ini tidak dapat dibatalkan.',
  'content.editor.deleteItem': 'Hapus {item}',
  'content.editor.publishing': 'Penerbitan',
  'content.editor.statusDraft': 'Draf — disembunyikan dari situs',
  'content.editor.statusPublished': 'Terbit — tampil di situs',
  'content.editor.statusArchived': 'Diarsipkan — disembunyikan, disimpan sebagai arsip',
  'content.editor.slug': 'Slug URL',
  'content.editor.slugHint': 'Harus unik di antara {material} atau di antara {finish}.',
  'content.editor.homeSlug': 'Slug “home” ditampilkan di halaman utama situs.',
  'content.editor.firstPublished': 'Pertama kali terbit {date}',
  'content.editor.back': 'Kembali',

  'content.seo.title': 'Pencarian & Media Sosial',
  'content.seo.description': 'Tampilan di Google dan saat dibagikan.',
  'content.seo.untitled': 'Tanpa judul',
  'content.seo.addDescription': 'Tambahkan deskripsi agar hasil pencarian menampilkan ringkasan.',
  'content.seo.metaTitle': 'Judul meta',
  'content.seo.metaTitleHint': 'Kosongkan untuk memakai judul halaman.',
  'content.seo.metaDescription': 'Deskripsi meta',
  'content.seo.shareImage': 'Gambar untuk dibagikan',
  'content.seo.shareImageHint': 'Disarankan 1200 × 630. Jika kosong, memakai gambar utama.',
  'content.seo.canonicalPath': 'Path kanonis',
  'content.seo.canonicalHint': 'Isi hanya jika konten ini utamanya berada di URL lain.',
  'content.seo.noindex': 'Sembunyikan dari mesin pencari (noindex)',

  'content.faq.title': 'FAQ',
  'content.faq.description': 'Ditampilkan sebagai akordeon dan ditambahkan ke hasil pencarian FAQ di Google.',
  'content.faq.add': '+ Tambah FAQ',
  'content.faq.empty': 'Belum ada FAQ.',
  'content.faq.questionN': 'Pertanyaan {n}',
  'content.faq.remove': 'Hapus',
  'content.faq.question': 'Pertanyaan',
  'content.faq.answer': 'Jawaban',

  'content.localized.fallsBackTo': 'Jika kosong, memakai {language}',
  'content.localized.richPlaceholder': 'Kosong — versi {language} ditampilkan di situs',
  'content.localized.startFrom': 'Mulai dari teks {language}',

  'content.section.capabilityStrip': 'Lencana kemampuan',
  'content.section.overview': 'Ikhtisar',
  'content.section.children': 'Produk dalam kategori ini',
  'content.section.structures': 'Struktur',
  'content.section.gallery': 'Galeri',
  'content.section.swatches': 'Sampel bahan & finishing',
  'content.section.materialsFinishes': 'Bahan & finishing',
  'content.section.specs': 'Spesifikasi',
  'content.section.cost': 'Catatan biaya',
  'content.section.design': 'Kustomisasi',
  'content.section.applications': 'Penggunaan',
  'content.section.process': 'Proses',
  'content.section.why': 'Mengapa kami (poin kepercayaan)',
  'content.section.faq': 'FAQ',
  'content.section.related': 'Artikel terkait',
  'content.section.cta': 'Ajakan bertindak (CTA)',

  'content.specGroup.rigid': 'Kotak rigid',
  'content.specGroup.label': 'Label',
  'content.specGroup.carton': 'Karton lipat',
  'content.specGroup.corrugated': 'Karton gelombang',
  'content.specGroup.bag': 'Tas kertas',
  'content.specGroup.nonwoven': 'Tas non-woven',
  'content.specGroup.tube': 'Tabung kertas',
  'content.specGroup.component': 'Komponen cetak',

  'content.details.specsEmpty': 'Tambahkan baris seperti Bahan / greyboard 1200gsm.',
  'content.details.label': 'Label',
  'content.details.value': 'Nilai',
  'content.details.addSpecRow': '+ Baris spesifikasi',
  'content.details.headerPanel': 'Header Halaman',
  'content.details.headerPanelHint': 'H1 dan subjudul di bagian atas halaman, beserta strip kemampuan.',
  'content.details.heading': 'Judul (H1)',
  'content.details.headingHint': 'Kosongkan untuk memakai nama.',
  'content.details.subtitle': 'Subjudul',
  'content.details.retailPanel': 'Detail produk',
  'content.details.codeLine': 'Baris kode',
  'content.details.codeLineHint': 'Baris kecil abu-abu di bawah nama, mis. "AUR-R101 · Emas putih 18K".',
  'content.details.includes': 'Termasuk di setiap pembelian',
  'content.details.includesHint': 'Poin singkat di samping spesifikasi, mis. "Pengiriman berasuransi".',
  'content.details.badges': 'Lencana kemampuan',
  'content.details.badge': 'Lencana',
  'content.details.addBadge': '+ Lencana',
  'content.details.overviewPanel': 'Ikhtisar',
  'content.details.paragraph': 'Paragraf',
  'content.details.addParagraph': '+ Paragraf',
  'content.details.structuresPanel': 'Struktur',
  'content.details.structure': 'Struktur',
  'content.details.addStructure': '+ Struktur',
  'content.details.materialsFinishesPanel': 'Bahan & Finishing',
  'content.details.materials': 'Bahan',
  'content.details.material': 'Bahan',
  'content.details.addMaterial': '+ Bahan',
  'content.details.finishes': 'Finishing',
  'content.details.finish': 'Finishing',
  'content.details.addFinish': '+ Finishing',
  'content.details.swatchGroups': 'Kelompok sampel',
  'content.details.specsPanel': 'Spesifikasi & Kustomisasi',
  'content.details.specs': 'Spesifikasi',
  'content.details.customisation': 'Opsi kustomisasi',
  'content.details.option': 'Opsi',
  'content.details.addOption': '+ Opsi',
  'content.details.costNote': 'Catatan biaya',
  'content.details.applicationsPanel': 'Penggunaan, Proses & Kepercayaan',
  'content.details.applications': 'Penggunaan',
  'content.details.application': 'Penggunaan',
  'content.details.addApplication': '+ Penggunaan',
  'content.details.processSteps': 'Langkah proses',
  'content.details.step': 'Langkah',
  'content.details.addStep': '+ Langkah',
  'content.details.trust': 'Mengapa kami (poin kepercayaan)',
  'content.details.point': 'Poin',
  'content.details.addPoint': '+ Poin',
  'content.details.layoutPanel': 'Tata Letak Halaman',
  'content.details.layoutHint': 'Bagian ditampilkan sesuai urutan ini. Bagian yang kosong dilewati.',
  'content.details.relatedProducts': 'Produk terkait',
  'content.details.relatedProductsHint': 'Path katalog seperti /products/labels. Judul mengikuti halaman yang ditautkan.',
  'content.details.pageN': 'Halaman {n}',
  'content.details.addRelatedPage': '+ Halaman terkait',
  'content.details.relatedArticles': 'Artikel terkait',

  'content.form.basics': 'Dasar',
  'content.form.pageTitle': 'Judul halaman',
  'content.form.pageTitleHint': 'Dipakai di tab browser dan sebagai judul SEO bawaan.',

  'content.form.article': 'Artikel',
  'content.form.excerpt': 'Kutipan',
  'content.form.excerptHint': 'Tampil di kartu daftar. Dibuat otomatis dari isi artikel jika kosong.',
  'content.form.body': 'Isi',
  'content.form.details': 'Detail',
  'content.form.section': 'Bagian',
  'content.form.sectionJournal': 'Jurnal (panduan & wawasan)',
  'content.form.sectionNews': 'Berita (kabar perusahaan)',
  'content.form.categoryLabel': 'Label kategori',
  'content.form.categoryLabelPlaceholder': 'Panduan',
  'content.form.author': 'Penulis',
  'content.form.publishDate': 'Tanggal terbit',
  'content.form.publishDateHint': 'Tanggal di masa depan akan menjadwalkan artikel. Kosong = saat pertama kali terbit.',
  'content.form.featured': 'Unggulan',

  'content.form.category': 'Kategori',
  'content.form.chooseCategory': 'Pilih kategori',
  'content.form.nothingToChoose': 'Belum ada pilihan.',

  'content.form.product': 'Produk',
  'content.form.productSummaryHint': 'Teks singkat di kartu pada halaman kategori dan deskripsi meta bawaan.',
  'content.form.organisation': 'Pengelompokan',
  'content.form.sortOrderHint': 'Angka lebih kecil tampil lebih dulu.',
  'content.form.industries': 'Industri',

  'content.form.categoryPanel': 'Kategori',
  'content.form.tagline': 'Tagline',
  'content.form.taglineHint': 'Satu baris singkat di bawah nama pada grid produk.',
  'content.form.ordering': 'Pengurutan',
  'content.form.categoryOrderHint': 'Kategori diberi nomor 01, 02, … sesuai urutan ini.',

  'content.form.industry': 'Industri',
  'content.form.risk': 'Risiko kemasan',
  'content.form.riskHint': 'Satu baris yang tampil di grid industri.',
  'content.form.cardLine': 'Baris kartu',
  'content.form.cardLineHint': 'Satu baris singkat di bawah nama pada halaman ringkasan.',
  'content.form.noteRetail': 'Catatan',
  'content.form.noteRetailHint': 'Hal yang perlu diketahui, mis. perawatan atau cara pemakaiannya.',
  'content.form.recommendedCategories': 'Kategori rekomendasi',
  'content.form.recommendedProducts': 'Produk rekomendasi',

  'content.form.swatch': 'Sampel',
  'content.form.swatchDescriptionHint': 'Satu baris di bawah nama.',
  'content.form.note': 'Catatan untuk pembeli',
  'content.form.noteHint': 'Kapan sebaiknya dipilih, dan hal yang perlu diperhatikan.',
  'content.form.photo': 'Foto',
  'content.form.guideLink': 'Tautan panduan (opsional)',
  'content.form.guideLinkText': 'Teks tautan panduan',
  'content.form.guideLinkTextPlaceholder': 'Baca panduan foil stamping',
  'content.form.whereItAppears': 'Tampil di',
  'content.form.whereItAppearsHint': 'Halaman produk, kategori, dan industri menampilkan sampel sesuai kelompok kemasannya.',
  'content.form.recommendedRank': 'Peringkat rekomendasi',
  'content.form.recommendedRankHint': '0 = tampil paling atas; kosong = bukan pilihan unggulan',
  'content.form.library': 'Pustaka',
  'content.form.kind': 'Jenis',
  'content.form.kindMaterial': 'Bahan (karton, kertas, film…)',
  'content.form.kindFinish': 'Finishing (foil, emboss, pelapis…)',
  'content.form.groupKey': 'Kunci grup',
  'content.form.groupKeyHint': 'Item dengan kunci yang sama ditampilkan bersama di bawah satu judul di website.',
  'content.form.swatchSortHint': 'Angka lebih kecil lebih dulu.',
}
